import { format, parseISO } from 'date-fns';

import Typography from 'components/Typography';

import styles from '../RequestDetails.module.scss';
import Button from 'components/Button';
import { useHistory } from 'react-router-dom';
import api from 'api';
import toast from 'react-hot-toast';
import { get } from 'lodash';
import { useCallback, useState } from 'react';
import DeleteModal from 'components/DeleteModal';
import IconButton from 'components/IconButton';
import ActionMenuPopup from './ActionMenuPopup';
import { RequestDetails, MMSubStatus, Order } from 'types';
import StatusBadge from 'components/StatusBadge';
import FeatureGuard from 'components/FeatureGuard';
import { ShopifyBrandIcon } from 'pages/Settings/Integrations/icons';
import { mapMMStatusText, mapMMSubStatusColor } from 'constants/mappings';
import PriorityFlag from 'components/PriorityFlag';

interface Props {
  onClickNotify: () => void;
  onCreatePurchaseOrder: () => void;
  onSendToDispatch: (
    newValue: any,
    actionMeta: any,
    originalId: string | number
  ) => void;
  refetchRequest: () => void;
  order: RequestDetails;
}

function Header({
  order,
  onClickNotify,
  onCreatePurchaseOrder,
  onSendToDispatch,
  refetchRequest,
}: Props) {
  const [deleteVisible, setIsDeleteVisible] = useState(false);
  const {
    sequential_order_number,
    id,
    created_at,
    order_type,
    sync_source,
    next_id,
    previous_id,
    pickup_dropoffs = [],
  } = order;
  const drugId = order.prescriptions[0]?.id;
  const status = order.prescriptions[0]?.status;
  const priority = order.priority as Order['priority'] | null;
  const datetime = parseISO(created_at);
  const { goBack, push } = useHistory();
  const deleteOrder = async () => {
    try {
      await api.delete(`/requests/${id}/delete/`);
      setIsDeleteVisible(false);
      goBack();
    } catch (error) {
      setIsDeleteVisible(false);
      toast.error(get(error, 'response.data.error_message', 'Error'));
    }
  };
  const renderOrderTypeBadge = useCallback(() => {
    return (
      <StatusBadge
        color={order_type === 'dropoff' ? 'green' : 'default'}
        className={styles.orderTypeBadge}
      >
        {order_type === 'dropoff' ? 'Dropoff' : 'Pickup'}
      </StatusBadge>
    );
  }, [order_type]);



  const handleSendToDispatch = () => {
    onSendToDispatch(
      { value: 'ready_for_dispatch' },
      { name: `drug-${drugId}` },
      id
    );
    refetchRequest();
    setTimeout(() => {
      push('/deliveries/delivery_planner');
    }, 1000);
  };
  return (
    <>
      <div className={styles.header}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div>
            <div className={styles.title_wrapper}>
              <Typography className={styles.title} variant="h2">
                Order&nbsp;#{sequential_order_number}&nbsp;
                <FeatureGuard feature="hasOrderTypeOnOrderDetails">
                  {renderOrderTypeBadge()}
                </FeatureGuard>
                <StatusBadge
                  className={styles.orderTypeBadge}
                  color={mapMMSubStatusColor[status as MMSubStatus]}
                >
                  {mapMMStatusText[status as MMSubStatus]}
                </StatusBadge>
                {!!priority && <PriorityFlag priority={priority} />}
                {sync_source && sync_source === 'shopify' && (
                  <span className={styles.subtitle}>
                    <ShopifyBrandIcon width={28} height={28} />
                  </span>
                )}
              </Typography>
            </div>

            <Typography className={styles.subtitle} variant="h5">
              {format(datetime, 'MMMM d, yyyy')}
            </Typography>
          </div>
          <div className={styles.buttons}>
            <IconButton
              icon="arrow_left"
              disabled={!previous_id}
              onClick={() => push(`/orders/${previous_id}`)}
            />
            <IconButton
              icon="arrow_right"
              disabled={!next_id}
              onClick={() => push(`/orders/${next_id}`)}
            />
          </div>
        </div>
        <div className={styles.buttons}>
          <FeatureGuard feature="hasPurchaseOrderOnOrderDetails">
            {order_type === 'dropoff' && (
              <Button
                color="blue"
                disabled={pickup_dropoffs?.length > 0}
                onClick={onCreatePurchaseOrder}
              >
                Create Pickup Order
              </Button>
            )}
          </FeatureGuard>
          <Button color="blue" onClick={() => onClickNotify()}>
            Message Customer
          </Button>
          <Button color="blue" onClick={handleSendToDispatch}>
            Send to Dispatch
          </Button>

          <ActionMenuPopup onDeleteOrder={() => setIsDeleteVisible(true)} />
        </div>
        <DeleteModal
          openModal={deleteVisible}
          setOpenModal={setIsDeleteVisible}
          onDelete={deleteOrder}
          text="Are you sure you want to delete this order?"
        />
      </div>

    </>
  );
}

export default Header;
